import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { AppRoutes } from '../routes/app.routes.enum';
import { DrillsRoutes } from '../routes/drills.routes.enum';
import { PlanRoutes } from '../routes/plan.routes.enum';
import { TranslationService } from '../services/translation.service';
import { QuickStartDto } from './dto/quick-start.dto';
import { QuickStartAction } from './quick-start-action.type';
import { QuickStartHttpService } from './quick-start-http.service';
import { toQuickStartViewModel } from './quick-start.mapper';
import { QuickStartViewModel } from './models/quick-start.view-model';

@Injectable({ providedIn: 'root' })
export class QuickStartSheetService {
  private readonly http: QuickStartHttpService = inject(QuickStartHttpService);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslationService = inject(TranslationService);

  private readonly _open: WritableSignal<boolean> = signal<boolean>(false);
  private readonly _viewModel: WritableSignal<QuickStartViewModel | null> = signal<QuickStartViewModel | null>(null);
  private readonly _loading: WritableSignal<boolean> = signal<boolean>(false);

  readonly open: Signal<boolean> = this._open.asReadonly();
  readonly viewModel: Signal<QuickStartViewModel | null> = this._viewModel.asReadonly();
  readonly loading: Signal<boolean> = this._loading.asReadonly();
  readonly isActive: Signal<boolean> = computed((): boolean => this._open());

  show(): void {
    if (this._open()) return;
    this._open.set(true);
    this._loading.set(true);
    this.http
      .get()
      .pipe(take(1))
      .subscribe({
        next: (dto: QuickStartDto): void => {
          this._viewModel.set(toQuickStartViewModel(dto, this.translate));
          this._loading.set(false);
        },
        error: (): void => {
          this._loading.set(false);
        }
      });
  }

  hide(): void {
    this._open.set(false);
  }

  dispatch(action: QuickStartAction): void {
    this.hide();
    switch (action.kind) {
      case 'start-today-plan':
        void this.router.navigate(['/', AppRoutes.PLAN, PlanRoutes.DETAIL, action.planId]);
        return;
      case 'start-from-drills':
        void this.router.navigate(['/', AppRoutes.DRILLS]);
        return;
      case 'repeat-last-drill':
        void this.router.navigate(['/', AppRoutes.DRILLS, DrillsRoutes.DETAIL, action.drillId]);
        return;
      case 'create-plan':
        void this.router.navigate(['/', AppRoutes.PLAN, PlanRoutes.CREATE]);
        return;
      case 'create-drill':
        void this.router.navigate(['/', AppRoutes.DRILLS, DrillsRoutes.CREATE]);
        return;
    }
  }
}
